package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.ActivityEvent
import com.dotdrives.model.Drive
import com.dotdrives.model.DriveFile
import com.dotdrives.model.DriveFinancials
import com.dotdrives.model.DriveTaskItem
import com.dotdrives.model.Note
import com.dotdrives.repository.DriveRepository
import com.dotdrives.repository.GiverRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch

enum class DriveDetailTab {
    Overview,
    Tasks,
    Notes,
    Files,
    Activity,
}

data class DriveDetailUiState(
    val loading: Boolean = false,
    val drive: Drive? = null,
    val financials: DriveFinancials? = null,
    val tasks: List<DriveTaskItem> = emptyList(),
    val files: List<DriveFile> = emptyList(),
    val activity: List<ActivityEvent> = emptyList(),
    val notes: List<Note> = emptyList(),
    val tab: DriveDetailTab = DriveDetailTab.Overview,
    val noteDraftTitle: String = "",
    val noteDraftBody: String = "",
    val savingNote: Boolean = false,
    val error: String? = null,
)

class DriveDetailController(
    private val driveRepo: DriveRepository,
    private val giverRepo: GiverRepository,
    scope: CoroutineScope,
    private val orgId: String,
) : DotController<DriveDetailUiState>(DriveDetailUiState(), scope) {
    private var loadGeneration = 0L
    private var openedDriveId: String? = null

    fun open(driveId: String) {
        openedDriveId = driveId
        val generation = nextGeneration()
        update { DriveDetailUiState(loading = true, tab = DriveDetailTab.Overview) }
        scope.launch {
            try {
                val loaded = coroutineScope {
                    val driveDeferred = async { driveRepo.get(orgId, driveId) }
                    val tasksDeferred = async { driveRepo.tasks(orgId, driveId) }
                    val filesDeferred = async { driveRepo.files(orgId, driveId) }
                    val activityDeferred = async { driveRepo.activity(orgId, driveId) }
                    val drive = driveDeferred.await().getOrThrow()
                    val financials = driveRepo.financials(orgId, listOf(driveId))
                        .getOrElse { emptyMap() }[driveId]
                    val notes = giverRepo.notes(orgId, drive.giverId).getOrElse { emptyList() }
                    DriveDetailLoad(
                        drive = drive,
                        financials = financials,
                        tasks = tasksDeferred.await().getOrThrow(),
                        files = filesDeferred.await().getOrThrow(),
                        activity = activityDeferred.await().getOrThrow(),
                        notes = notes,
                    )
                }
                if (!isCurrent(generation)) return@launch
                update { current ->
                    if (isCurrent(generation)) {
                        current.copy(
                            loading = false,
                            drive = loaded.drive,
                            financials = loaded.financials,
                            tasks = loaded.tasks,
                            files = loaded.files,
                            activity = loaded.activity,
                            notes = loaded.notes,
                            error = null,
                        )
                    } else {
                        current
                    }
                }
            } catch (error: Throwable) {
                publishError(error, generation)
            }
        }
    }

    fun retry() {
        openedDriveId?.let(::open)
    }

    fun setTab(tab: DriveDetailTab) {
        update { it.copy(tab = tab) }
    }

    fun toggleTask(taskId: String, isComplete: Boolean) {
        val driveId = openedDriveId ?: return
        val previousTasks = state.value.tasks
        update {
            it.copy(
                tasks = it.tasks.map { task -> if (task.id == taskId) task.copy(isComplete = isComplete) else task },
                error = null,
            )
        }
        scope.launch {
            driveRepo.setTaskComplete(orgId, driveId, taskId, isComplete)
                .onSuccess { updated ->
                    if (openedDriveId != driveId) return@onSuccess
                    update {
                        it.copy(tasks = it.tasks.map { task -> if (task.id == taskId) updated else task })
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    if (openedDriveId != driveId) return@onFailure
                    update { it.copy(tasks = previousTasks, error = error.message ?: "Something went wrong") }
                }
        }
    }

    fun updateNoteDraft(title: String, body: String) {
        update { it.copy(noteDraftTitle = title, noteDraftBody = body, error = null) }
    }

    fun saveNote() {
        val current = state.value
        val drive = current.drive ?: return
        if (current.savingNote) return
        if (current.noteDraftBody.isBlank()) {
            update { it.copy(error = "Note body is required") }
            return
        }
        val title = current.noteDraftTitle.trim()
        val body = current.noteDraftBody.trim()
        update { it.copy(savingNote = true, error = null) }
        scope.launch {
            giverRepo.addNote(orgId, drive.giverId, title, body)
                .onSuccess { note ->
                    if (openedDriveId != drive.id) return@onSuccess
                    update {
                        it.copy(
                            notes = listOf(note) + it.notes,
                            noteDraftTitle = "",
                            noteDraftBody = "",
                            savingNote = false,
                            error = null,
                        )
                    }
                }
                .onFailure { error ->
                    if (error is CancellationException) throw error
                    if (openedDriveId != drive.id) return@onFailure
                    update { it.copy(savingNote = false, error = error.message ?: "Something went wrong") }
                }
        }
    }

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        update { current ->
            if (isCurrent(generation)) {
                current.copy(loading = false, error = error.message ?: "Something went wrong")
            } else {
                current
            }
        }
    }

    private fun nextGeneration(): Long {
        loadGeneration += 1
        return loadGeneration
    }

    private fun isCurrent(generation: Long): Boolean = generation == loadGeneration
}

private data class DriveDetailLoad(
    val drive: Drive,
    val financials: DriveFinancials?,
    val tasks: List<DriveTaskItem>,
    val files: List<DriveFile>,
    val activity: List<ActivityEvent>,
    val notes: List<Note>,
)
